import { Clipboard } from '@capacitor/clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { setPendingLoginHint } from 'probonio-shared-ui/module/auth';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MessageLayout from '../component/layout/MessageLayout';
import { useAppSelector } from '../redux/hooks';

const AccountMergedPage: React.FC = () => {
  const { t } = useTranslation('accountMergedPage');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const withMessage = useWithMessage();
  const accountMergeMail = useAppSelector(state => state.me.accountMergeMail);

  const handleCopyMail = useCallback(() => {
    if (!accountMergeMail) {
      return;
    }
    Clipboard.write({ label: t('mailLabel'), string: accountMergeMail })
      .then(() => enqueueSnackbar(t('copiedToast'), { variant: 'success' }))
      .catch(withMessage);
  }, [accountMergeMail, enqueueSnackbar, t, withMessage]);

  const handleGoToLogin = useCallback(() => {
    if (accountMergeMail) {
      setPendingLoginHint(accountMergeMail);
    }
    navigate('/logout', { replace: true });
  }, [accountMergeMail, navigate]);

  return (
    <MessageLayout
      title={t('title')}
      text={<Trans t={t} i18nKey={'details'} />}
      actions={
        <Stack spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
          {accountMergeMail ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                maxWidth: '100%',
                paddingX: 1.5,
                paddingY: 0.5,
                borderRadius: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography
                variant="body2"
                className="selectable"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                data-test-id="account-merged-mail"
              >
                {accountMergeMail}
              </Typography>
              <IconButton size="small" aria-label={t('copyToClipboard')} onClick={handleCopyMail} data-test-id="account-merged-copy">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}
          <Button
            variant="contained"
            color="primary"
            aria-label={t('toLogin')}
            onClick={handleGoToLogin}
            sx={{ paddingX: 4 }}
            data-test-id="account-merged-login-button"
          >
            {t('toLogin')}
          </Button>
        </Stack>
      }
    />
  );
};

export default AccountMergedPage;
